/**
 * DrillHistoryTab Component
 * Displays historical drill records with filtering
 */

import React, { useState, useMemo } from 'react';
import {
  ClipboardList,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Download,
  Search
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { getDrillDisplayName } from './utils/drillsHelpers';
import Dropdown from '../../../common/Dropdown';
import AttributionBadge from '../../shared/AttributionBadge';
import { normalizeOutcome } from '../../../../utils/drillHelpers';

const DrillHistoryTab = ({ drills, onViewDrill, onExport }) => {  // FT-387: onExport wires export button to DrillsModule handleExportReport
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterOutcome, setFilterOutcome] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get drill image
  const getDrillImage = (drillType) => {
    const type = (drillType || '').toLowerCase();
    
    // Specific drill images
    if (type.includes('fire')) {
      return '/images/drills/fire-drill.jpg';
    }
    if (type.includes('abandon') || type.includes('ship')) {
      return '/images/drills/abandon-ship.jpg';
    }
    if (type.includes('man_overboard') || type.includes('mob') || 
        (type.includes('man') && type.includes('overboard'))) {
      return '/images/drills/man-overboard.jpg';
    }
    
    // Default drill category image
    return '/images/drills/drill-category.jpg';
  };

  // Get unique drill types
  const drillTypes = useMemo(() => {
    const types = new Set(drills.map(d => d.type));
    return Array.from(types).sort();
  }, [drills]);

  // Create type filter options for Dropdown
  const typeFilterOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Types' }];
    drillTypes.forEach(type => {
      options.push({
        value: type,
        label: getDrillDisplayName(type)
      });
    });
    return options;
  }, [drillTypes]);

  // Outcome filter options
  const outcomeFilterOptions = [
    { value: 'all', label: 'All Outcomes' },
    { value: 'successful', label: 'Successful' },
    { value: 'failed', label: 'Failed' }
  ];

  // Filter drills
  const filteredDrills = useMemo(() => {
    return drills.filter(drill => {
      // FT-91: Filter out drill register config records (they have 'drill:' prefix IDs or no date field)
      // Config records have format: { id: "drill:uuid", drillType: "fire", frequencyDays: 30, ... }
      // Real drill records have: { id: "DR003-26", type: "fire", date: "2026-03-29", ... }
      if (drill.id?.startsWith('drill:') || !drill.date) {
        return false;
      }

      const matchesSearch = searchTerm === '' ||
        drill.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        drill.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        drill.notes?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filterType === 'all' || drill.type === filterType;
      // FT-91: Use normalizeOutcome for case-insensitive outcome comparison
      const matchesOutcome = filterOutcome === 'all' || normalizeOutcome(drill.outcome) === filterOutcome;

      return matchesSearch && matchesType && matchesOutcome;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [drills, searchTerm, filterType, filterOutcome]);

  // Pagination
  const totalPages = Math.ceil(filteredDrills.length / itemsPerPage);
  const paginatedDrills = filteredDrills.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats = {
      total: drills.length,
      successful: drills.filter(d => normalizeOutcome(d.outcome) === 'successful').length,
      failed: drills.filter(d => normalizeOutcome(d.outcome) === 'failed').length,
      thisMonth: 0,
      lastMonth: 0
    };

    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    drills.forEach(drill => {
      const drillDate = new Date(drill.date);
      if (drillDate.getMonth() === thisMonth && drillDate.getFullYear() === thisYear) {
        stats.thisMonth++;
      } else if (drillDate.getMonth() === lastMonth && drillDate.getFullYear() === lastMonthYear) {
        stats.lastMonth++;
      }
    });

    return stats;
  }, [drills]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || filterType !== 'all' || filterOutcome !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterOutcome('all');
  };

  const getOutcomeIcon = (outcome) => {
    return normalizeOutcome(outcome) === 'successful' ? CheckCircle : XCircle;
  };

  const getOutcomeColor = (outcome) => {
    return normalizeOutcome(outcome) === 'successful' ? 'green' : 'red';
  };

  // FT-252: Single combined status pill for mobile card
  // FT-394: include 'pending' sign-off state
  const getMobileStatusBadge = (drill) => {
    const outcome = normalizeOutcome(drill.outcome);
    if (outcome === 'failed') {
      return <Badge color="red">Failed</Badge>;
    }
    if (drill.reviewStatus === 'approved') {
      return <Badge color="green">Signed Off</Badge>;
    }
    if (drill.reviewStatus === 'pending') {
      return <Badge color="yellow">Awaiting Sign-Off</Badge>;
    }
    return <Badge color="yellow">Successful</Badge>;
  };

  // FT-358: register middle-slot bottom-bar action per brief §6
  // (Drills History: [Export] only). Placeholder no-op preserved from
  // pre-FT-358 TODO.
  useSetBottomBarActions([
    {
      key: 'drills-history-export',
      icon: Download,
      label: 'Export',
      onClick: () => onExport && onExport(),  // FT-387: wire to DrillsModule handleExportReport
    },
  ]);

  return (
    <div className="pb-20 lg:pb-0">
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        {/* Header with slate-600 background and search inside */}
        <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
          <div className="flex items-center justify-between gap-6">
            {/* Title */}
            <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
              Drill History
            </h3>

            {/* Search Input */}
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search drill history..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-5">
          {/* Filters */}
          <div className="space-y-3 mb-6">
            {/* Row 1: Filter Dropdowns - Side by side */}
            <div className="flex gap-3">
              {/* Type Filter Dropdown */}
              <Dropdown
                value={filterType}
                onChange={setFilterType}
                width="flex-1 sm:flex-none sm:w-36"
                options={typeFilterOptions}
              />

              {/* Outcome Filter Dropdown */}
              <Dropdown
                value={filterOutcome}
                onChange={setFilterOutcome}
                width="flex-1 sm:flex-none sm:w-36"
                options={outcomeFilterOptions}
              />
            </div>
          </div>
          {/* Drill Records */}
          <div className="divide-y divide-gray-200">
            {paginatedDrills.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {hasActiveFilters
                    ? 'No drill records match your filters'
                    : 'No drill records found'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-sm text-cyan-600 hover:text-cyan-500"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              paginatedDrills.map(drill => {
                const OutcomeIcon = getOutcomeIcon(drill.outcome);
                return (
                  <div
                    key={drill.id}
                    className="px-4 md:px-6 py-3 md:py-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => onViewDrill(drill)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 min-w-0 flex-1">
                        {/* FT-252: Drill image hidden on mobile to save space */}
                        <img
                          src={getDrillImage(drill.type)}
                          alt={getDrillDisplayName(drill.type)}
                          className="hidden md:block w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover shadow-sm flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div
                          className="hidden md:flex w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gray-100 items-center justify-center flex-shrink-0"
                          style={{ display: 'none' }}
                        >
                          <OutcomeIcon
                            className={`h-6 w-6 md:h-7 md:w-7 ${
                              normalizeOutcome(drill.outcome) === 'successful' ? 'text-green-600' : 'text-red-600'
                            }`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {getDrillDisplayName(drill.type)}
                          </p>
                          {/* FT-252: Mobile shows date only */}
                          <div className="md:hidden flex items-center mt-1 text-xs text-gray-500">
                            <Calendar className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                            {new Date(drill.date).toLocaleDateString()}
                          </div>
                          {/* Desktop: full meta row */}
                          <div className="hidden md:flex items-center mt-1 space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(drill.date).toLocaleDateString()}
                            </span>
                            <span className="flex items-center">
                              <Users className="h-4 w-4 mr-1" />
                              {drill.participants?.length || 0} participants
                            </span>
                            {drill.location && (
                              <span>{drill.location}</span>
                            )}
                          </div>
                          {/* FT-06a: Attribution display — desktop only (FT-252) */}
                          <div className="hidden md:block">
                            <AttributionBadge createdBy={drill.createdBy} createdAt={drill.createdAt} className="mt-1" />
                          </div>
                        </div>
                      </div>
                      {/* FT-252: Mobile shows single combined status pill; desktop keeps split badges */}
                      <div className="flex-shrink-0">
                        <div className="md:hidden">
                          {getMobileStatusBadge(drill)}
                        </div>
                        <div className="hidden md:flex flex-col items-end gap-1">
                          <Badge color={getOutcomeColor(drill.outcome)}>
                            {normalizeOutcome(drill.outcome) === 'successful' ? 'Successful' : 'Failed'}
                          </Badge>
                          {/* FT-394: sign-off status badge */}
                          {drill.reviewStatus === 'approved' ? (
                            <Badge color="green">Signed Off</Badge>
                          ) : drill.reviewStatus === 'pending' ? (
                            <Badge color="yellow">Awaiting Sign-Off</Badge>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredDrills.length)} of{' '}
                {filteredDrills.length} results
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </div>
  );
};

export default DrillHistoryTab;